import type { FC } from "react";

import { filterByCategory } from "@/shared/constants/estateCategories";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useEstateItemActions } from "@/shared/hooks/useEstateItemActions";
import { useUploadSupportingDocument } from "@/shared/hooks/useUploadSupportingDocument";
import { ReviewScreenLayout } from "@/pages/postloss/modules/components/ReviewScreenLayout";
import { useInsuranceReviewRedirect } from "@/pages/postloss/modules/hooks/useInsuranceReviewRedirect";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";

import {
  LtcInsuranceForm,
  parseItemToForm,
  buildMetadata,
  type LtcInsuranceFormValues,
} from "./LtcInsuranceForm";
import { buildLtcSubtitle } from "./insuranceSubtitles";

export const LtcInsuranceReview: FC<FlowCustomComponentProps> = ({
  onComplete,
  goToScreen,
  setAnswer,
  answers,
}) => {
  const { person } = useAppContext();
  const estateId = person?.estate_id;
  const { assets, isLoading, isFetching, updateItem, isUpdatingItem, deleteItem, itemActions } =
    useEstateItemActions(estateId);
  const { upload } = useUploadSupportingDocument();

  const policies = filterByCategory(assets, "Long-term care insurance");
  const firstName = person?.first_name ?? "their";
  const hasExtracted = policies.some((p) => p.extracted_from_document);

  const { willRedirect } = useInsuranceReviewRedirect({
    items: policies,
    isLoading,
    isFetching,
    answers,
    setAnswer,
    goToScreen,
    reviewKey: "ltc_insurance_review",
    manageKey: "ltc_insurance_manage",
    manageScreen: "ltc_insurance_manage",
    fallbackScreen: "has_ltc_insurance",
  });

  return (
    <ReviewScreenLayout<LtcInsuranceFormValues>
      items={policies}
      isLoading={isLoading}
      isFetching={isFetching}
      isRedirecting={willRedirect}
      screenTitle={
        hasExtracted
          ? "We found long-term care insurance in your documents"
          : `Review ${firstName}'s LTC insurance policies`
      }
      screenSubtitle="Review the details and add any others we might have missed."
      itemLabel="long-term care insurance policies"
      listTitle="LTC Insurance Policies"
      addButtonLabel="Add policy"
      editDialogTitle="Edit policy"
      deleteDialogTitle="Delete Policy"
      renderSubtitle={buildLtcSubtitle}
      isUpdatingItem={isUpdatingItem}
      deleteItem={deleteItem}
      itemActions={itemActions}
      onContinue={() => onComplete()}
      onAddAnother={() =>
        goToScreen("add_ltc_insurance", {
          ...answers,
          has_ltc_insurance: "yes",
          add_ltc_insurance: "yes",
          ltc_insurance_manage: "yes",
        })
      }
      parseItemToForm={(item) => parseItemToForm(item.item_name, item.balance, item.metadata)}
      renderEditForm={({ form, onChange, existingFileName, onFileRemove }) => (
        <LtcInsuranceForm
          form={form}
          onChange={onChange}
          existingDocumentFileName={existingFileName}
          onDocumentRemoved={onFileRemove}
        />
      )}
      onSaveEdit={async ({ id, form, fileRemoved }) => {
        if (!estateId) return;

        if (!form.companyName.trim()) {
          throw new Error("Please enter the insurance company name");
        }

        let sourceDocumentId: number | undefined;

        if (form.documentFile) {
          sourceDocumentId = await upload(form.documentFile, estateId, "ltc_insurance_policy");
        }

        const sourceDocUpdate = sourceDocumentId
          ? { source_document_id: sourceDocumentId }
          : fileRemoved
            ? { source_document_id: null }
            : {};

        await updateItem(id, {
          item_name: form.companyName.trim(),
          item_type: "Long-term care insurance",
          balance: parseFloat(form.dailyBenefitAmount) || 0,
          metadata: buildMetadata(form),
          ...sourceDocUpdate,
        });
      }}
    />
  );
};
